// Lightweight PWA helpers:
//  • registerServiceWorker() — installs /service-worker.js (mandatory for the
//    PWA-installable rule). Pass-through SW, no caching.
//  • setupInstallPromptCapture() — captures the `beforeinstallprompt` event so
//    a custom "Install app" button can call promptInstall() on user click.
//
// Why a custom button instead of relying on browser banner?
// Chrome shows an automatic install banner only after a strict engagement
// heuristic (multiple visits over days). With a custom button you can offer
// install on first visit — useful when admins/staff want every staff PC to
// have the app pinned.

let _deferredPrompt = null;
const listeners = new Set();

export function registerServiceWorker() {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    // Defer registration to idle so it doesn't compete with first paint.
    const reg = () => navigator.serviceWorker.register("/service-worker.js").catch((e) => {
        console.warn("[SW] registration failed:", e);
    });
    if (document.readyState === "complete") reg();
    else window.addEventListener("load", reg, { once: true });
}

export function setupInstallPromptCapture() {
    if (typeof window === "undefined") return;
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        _deferredPrompt = e;
        listeners.forEach((cb) => { try { cb(true); } catch { /* ignore */ } });
    });
    window.addEventListener("appinstalled", () => {
        _deferredPrompt = null;
        listeners.forEach((cb) => { try { cb(false); } catch { /* ignore */ } });
    });
}

export function canPromptInstall() {
    return _deferredPrompt !== null;
}

export function onInstallAvailableChange(callback) {
    listeners.add(callback);
    return () => listeners.delete(callback);
}

export async function promptInstall() {
    if (!_deferredPrompt) return { outcome: "unavailable" };
    try {
        _deferredPrompt.prompt();
        const choice = await _deferredPrompt.userChoice;
        _deferredPrompt = null;
        listeners.forEach((cb) => { try { cb(false); } catch { /* ignore */ } });
        return { outcome: choice?.outcome || "unknown" };
    } catch (e) {
        return { outcome: "error", error: String(e) };
    }
}

// iOS Safari has no `beforeinstallprompt` — install is via Share → Add to Home Screen.
// Detect so we can show iOS-specific hints.
export function isIOS() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
}

export function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}
